import React from 'react'
import { Box, Grid, Typography, Link as MuiLink, Divider, IconButton, SvgIcon } from '@mui/material'
import InstagramIcon from '@mui/icons-material/Instagram'
import FacebookIcon from '@mui/icons-material/Facebook'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import YouTubeIcon from '@mui/icons-material/YouTube'
import PhoneIcon from '@mui/icons-material/Phone'
import { useLocation, Link } from 'react-router-dom'
import { useContactDetails, toTelHref } from '../hooks/useContactDetails'

const Footer: React.FC = () => {
  const { phone } = useContactDetails()
  const location = useLocation()
  const isHome = location.pathname === '/'

  // Dynamic color scheme - dark footer on home page, light on other pages
  const backgroundColor = isHome ? '#1f1f1f' : 'white'
  const textColor = isHome ? 'rgba(255,255,255,0.87)' : 'text.primary'
  const subTextColor = isHome ? 'rgba(255,255,255,0.6)' : 'text.secondary'
  const dividerColor = isHome ? 'rgba(255,255,255,0.2)' : '#e0e0e0'

  return (
    // Main footer container - spans full width with dynamic background
    <Box component="footer" sx={{ backgroundColor, color: textColor, width: '100%' }}>
      {/* Content container - centered with max width for readability */}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
        <Grid container spacing={4}>
          {/* Left column - Company branding, tagline and phone */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Direct Connect Wales
            </Typography>
            <Typography variant="body2" sx={{ color: subTextColor, mb: 2 }}>
              Speed You Need. Fairness You Deserve. Security You Trust.
            </Typography>
            <Box
              component="a"
              href={toTelHref(phone)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: '#00B140',
                fontWeight: 800,
                fontSize: '1.35rem',
                letterSpacing: '0.01em',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <PhoneIcon sx={{ fontSize: 22 }} />
              {phone}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', color: subTextColor, mt: 0.5 }}>
              {'Mon\u2013Fri 8am\u20136pm \u00B7 Sat 9am\u20131pm'}
            </Typography>
          </Grid>
          
          {/* Middle column - Company navigation links */}
          <Grid item xs={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 2 }}>
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MuiLink component={Link} to="/about" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>About</MuiLink>
<MuiLink component={Link} to="/contact" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Contact</MuiLink>
            </Box>
          </Grid>
          
          {/* Right column - Legal and policy links */}
          <Grid item xs={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 2 }}>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MuiLink component={Link} to="/legal#terms" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Terms</MuiLink>
              <MuiLink component={Link} to="/legal#privacy" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Privacy</MuiLink>
              <MuiLink component={Link} to="/legal#cookies" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Cookie policy</MuiLink>
              <MuiLink component={Link} to="/legal#complaints-code" sx={{ color: textColor, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.9rem', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Complaints</MuiLink>
            </Box>
          </Grid>
        </Grid>

        {/* Divider line separating main content from bottom section */}
        <Divider sx={{ my: 1.5, borderColor: dividerColor }} />

        {/* Social Media Section - Prominently displayed */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
            Follow Us / Dilynwch Ni
          </Typography>
          <Typography variant="body2" sx={{ color: subTextColor, mb: 2, textAlign: 'center' }}>
            Stay connected with us on social media / Arhoswch yn gysylltiedig â ni ar y cyfryngau cymdeithasol
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* TODO: Replace href with the company's Instagram profile URL */}
            <IconButton
              component="a"
              href="https://instagram.com/directconnectwales"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: textColor,
                bgcolor: isHome ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#E1306C',
                  bgcolor: isHome ? 'rgba(225,48,108,0.1)' : 'rgba(225,48,108,0.1)',
                  transform: 'translateY(-4px) scale(1.1)'
                }
              }}
            >
              <InstagramIcon />
            </IconButton>

            {/* TODO: Replace href with the company's X/Twitter profile URL */}
            <IconButton
              component="a"
              href="https://twitter.com/directconnectwales"
              aria-label="X (formerly Twitter)"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: textColor,
                bgcolor: isHome ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#000000',
                  bgcolor: isHome ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  transform: 'translateY(-4px) scale(1.1)'
                }
              }}
            >
              {/* X brand mark (from simple-icons X path) */}
              <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 24 }}>
                <path d="M18.244 2H21.5l-7.42 8.53L22 22h-6.26l-4.86-6.86L5.62 22H2.36l7.83-9.03L2 2h6.26l4.6 6.66L18.244 2z" />
              </SvgIcon>
            </IconButton>

            {/* TODO: Replace href with the company's Facebook page URL */}
            <IconButton
              component="a"
              href="https://facebook.com/directconnectwales"
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: textColor,
                bgcolor: isHome ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#1877F2',
                  bgcolor: isHome ? 'rgba(24,119,242,0.1)' : 'rgba(24,119,242,0.1)',
                  transform: 'translateY(-4px) scale(1.1)'
                }
              }}
            >
              <FacebookIcon />
            </IconButton>

            {/* TODO: Replace href with the company's LinkedIn page URL */}
            <IconButton
              component="a"
              href="https://linkedin.com/company/directconnectwales"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: textColor,
                bgcolor: isHome ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#0A66C2',
                  bgcolor: isHome ? 'rgba(10,102,194,0.1)' : 'rgba(10,102,194,0.1)',
                  transform: 'translateY(-4px) scale(1.1)'
                }
              }}
            >
              <LinkedInIcon />
            </IconButton>

            {/* TODO: Replace href with the company's YouTube channel URL */}
            <IconButton
              component="a"
              href="https://youtube.com/@directconnectwales"
              aria-label="YouTube"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: textColor,
                bgcolor: isHome ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#FF0000',
                  bgcolor: isHome ? 'rgba(255,0,0,0.1)' : 'rgba(255,0,0,0.1)',
                  transform: 'translateY(-4px) scale(1.1)'
                }
              }}
            >
              <YouTubeIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 2, borderColor: dividerColor }} />

        {/* Bottom section with copyright */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
          {/* Legal identity (company name, number and registered office) */}
          <Typography variant="body2" sx={{ color: subTextColor, textAlign: 'center', fontSize: '0.8rem' }}>
            Direct Connect Wales is a trading name of M&amp;S Brain Holdings Ltd, registered in England and Wales, company no. 16643786. Registered office: 4th Floor, 14 Museum Place, Cardiff CF10 3BH.
          </Typography>
          {/* Copyright notice */}
          <Typography variant="body2" sx={{ color: subTextColor, textAlign: 'center' }}>
            © {new Date().getFullYear()} Direct Connect Wales Ltd. All rights reserved. / Cedwir pob hawl.
          </Typography>
        </Box>
      </Box>
    </Box>

    
  )
}

export default Footer


